import { shift, computePosition, offset } from "@floating-ui/dom"
import { useEffect } from "react"
import { debounce } from "../../utils/debounce"

const TOOLTIP_SHOW_DELAY = 400
const TOOLTIP_KEEP_VISIBLE_TIME = 200

function roundByDPR(value: number) {
  const dpr = window.devicePixelRatio || 1
  return Math.round(value * dpr) / dpr
}

let animationEvent: ((this: HTMLElement) => any) | null = null
// helper to reduce compute
let isTooltipVisible = false

let hideTooltipTimeoutRef: number | undefined = undefined
const debouncedShowTooltip = debounce(showTooltip, TOOLTIP_SHOW_DELAY)

function tooltipMouseOverHandler(e: MouseEvent) {
  if (!e.target) {
    return
  }

  // get the actual element that has the tooltip attribute
  const tooltipTarget = (e.target as HTMLElement).closest(
    "[data-tooltip]"
  ) as HTMLElement | null
  if (tooltipTarget) {
    const tooltip = document.getElementById("tooltip")

    if (tooltip) {
      debouncedShowTooltip(tooltip, tooltipTarget)
    }
  } else {
    const tooltip = document.getElementById("tooltip")
    debouncedShowTooltip.clear()

    if (tooltip && !hideTooltipTimeoutRef) {
      clearTimeout(hideTooltipTimeoutRef)
      hideTooltipTimeoutRef = setTimeout(() => {
        if (isTooltipVisible) {
          hideTooltip(tooltip)
        }
      }, TOOLTIP_KEEP_VISIBLE_TIME) as unknown as number
    }
  }
}

let isScrolling = false
function tooltipScrollHandler() {
  const tooltip = document.getElementById("tooltip")

  if (tooltip && isTooltipVisible && !isScrolling) {
    isScrolling = true
    debouncedShowTooltip.clear()
    hideTooltip(tooltip)
  }
}

function showTooltip(tooltipEl: HTMLElement, targetEl: HTMLElement) {
  clearTimeout(hideTooltipTimeoutRef)
  hideTooltipTimeoutRef = undefined

  // remove active animationend event
  if (animationEvent) {
    tooltipEl.removeEventListener("animationend", animationEvent)
    animationEvent = null
  }

  requestAnimationFrame(() => {
    // set tooltip text
    tooltipEl.textContent = targetEl.dataset.tooltip || ""

    // wait for content to be update and then compute position
    requestAnimationFrame(() => {
      let animationNameToUse = "legacy_tooltip_show"

      // if the tooltip is already visible we just update the tooltip location without an animation
      if (isTooltipVisible) {
        animationNameToUse = "none"
      }
      isTooltipVisible = true

      computePosition(targetEl, tooltipEl, {
        placement: "top",
        middleware: [shift(), offset(10)],
      }).then(({ x, y }) => {
        requestAnimationFrame(() => {
          // update tooltip location
          tooltipEl.style.setProperty(
            "--legacy-tooltip-coord-x",
            `${roundByDPR(x)}px`
          )
          tooltipEl.style.setProperty(
            "--legacy-tooltip-coord-y",
            `${roundByDPR(y)}px`
          )

          requestAnimationFrame(() => {
            // start animation
            tooltipEl.style.setProperty(
              "--legacy-tooltip-animation-name",
              animationNameToUse
            )

            animationEvent = function animationEventListener(
              this: HTMLElement
            ): void {
              this.removeEventListener("animationend", animationEventListener)

              // set to end state
              requestAnimationFrame(() => {
                this.style.transform = `translate3d(var(--legacy-tooltip-coord-x), var(--legacy-tooltip-coord-y), 0) scale3d(1, 1, 1)`
                this.style.opacity = "1"
                this.style.setProperty(
                  "--legacy-tooltip-animation-name",
                  "none"
                )
              })
            }
            if (animationNameToUse === "none") {
              animationEvent.call(tooltipEl)
            } else {
              tooltipEl.addEventListener("animationend", animationEvent)
            }
          })
        })
      })
    })
  })
}

function hideTooltip(tooltipEl: HTMLElement) {
  if (animationEvent) {
    tooltipEl.removeEventListener("animationend", animationEvent)
    animationEvent = null
  }

  requestAnimationFrame(() => {
    tooltipEl.style.setProperty(
      "--legacy-tooltip-animation-name",
      "legacy_tooltip_hide"
    )
    isTooltipVisible = false

    animationEvent = function animationEventListener(this: HTMLElement): void {
      this.removeEventListener("animationend", animationEventListener)
      animationEvent = null
      // reset to start state
      requestAnimationFrame(() => {
        this.style.transform = `translate3d(var(--legacy-tooltip-coord-x), var(--legacy-tooltip-coord-y), 0)`
        this.style.opacity = ``

        this.style.setProperty("--legacy-tooltip-coord-x", "-100%")
        this.style.setProperty("--legacy-tooltip-coord-y", "-100%")
        this.style.setProperty("--legacy-tooltip-animation-name", "none")
      })
    }
    tooltipEl.addEventListener("animationend", animationEvent)
  })
}

export function TooltipRenderer(): React.JSX.Element | null {
  useEffect(() => {
    const tooltip = document.createElement("div")
    tooltip.id = "tooltip"
    tooltip.role = "tooltip"
    tooltip.className = "legacy_tooltip"

    let rafRef: number | null = requestAnimationFrame(() => {
      rafRef = null
      document.body.appendChild(tooltip)
    })

    const resetScrollHandler = () => {
      isScrolling = false
    }

    document.addEventListener("mouseover", tooltipMouseOverHandler, {
      passive: true,
    })
    // necessary to remove tooltip on scroll - mouseover doesn't get triggered
    window.addEventListener("scroll", tooltipScrollHandler, {
      passive: true,
      capture: true,
    })

    // we only want to hide a tooltip
    window.addEventListener("scrollend", resetScrollHandler, {
      passive: true,
      capture: true,
    })

    return () => {
      if (rafRef) {
        cancelAnimationFrame(rafRef)
      } else {
        requestAnimationFrame(() => {
          document.body.removeChild(tooltip)
        })
      }

      document.removeEventListener("mouseover", tooltipMouseOverHandler)
      window.removeEventListener("scroll", tooltipScrollHandler)
      window.removeEventListener("scrollend", resetScrollHandler)
    }
  }, [])

  return null
}
